import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/atoms/card';
import { FormImageUpload } from '@/components/forms';
import { ImageCropper } from '@/features/advertising/components/upload/ImageCropper';

interface ImagePreview {
  src: string;
  isNew?: boolean;
  id?: number | string;
}

interface PendingCropImage {
  file: File;
  preview: string;
}

interface ProductImagesProps {
  previews: ImagePreview[];
  onAdd: (files: FileList) => void;
  onRemove: (index: number, id?: number | string) => void;
  onReorder?: (fromIndex: number, toIndex: number) => void;
  isCompressing?: boolean;
  maxImages?: number;
  // Cropping support
  pendingCropImage?: PendingCropImage | null;
  cropQueueLength?: number;
  onCropComplete?: (file: File, preview: string) => void;
  onCropCancel?: () => void;
}

export function ProductImages({
  previews,
  onAdd,
  onRemove,
  onReorder,
  isCompressing = false,
  maxImages = 5,
  // Cropping props
  pendingCropImage,
  cropQueueLength = 0,
  onCropComplete,
  onCropCancel,
}: ProductImagesProps) {
  const { t } = useTranslation();

  const handleCropComplete = (file: File, preview: string) => {
    onCropComplete?.(file, preview);
  };

  const handleCropCancel = () => {
    onCropCancel?.();
  };

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>{t('products.images')} *</CardTitle>
          <CardDescription>
            {t('products.imagesDescription')}
            <span className="block text-xs text-muted-foreground mt-1">
              Format carré (1:1) requis pour les images produits
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <FormImageUpload
            previews={previews}
            maxImages={maxImages}
            onAdd={onAdd}
            onRemove={onRemove}
            onReorder={onReorder}
            isCompressing={isCompressing}
            mainLabel={t('products.mainImage')}
            emptyStateTitle={t('products.clickToUpload')}
            emptyStateDescription={t('products.maxImages')}
            addMoreLabel={t('products.addMore')}
            newBadgeLabel={t('common.new', { defaultValue: 'New' })}
            reorderHint={t('products.dragToReorder', { defaultValue: 'Drag to reorder' })}
          />
        </CardContent>
      </Card>

      {/* Image Cropper Modal - Forces 1:1 aspect ratio */}
      {pendingCropImage && onCropComplete && (
        <ImageCropper
          open={!!pendingCropImage}
          onOpenChange={(open) => {
            if (!open) handleCropCancel();
          }}
          imageSrc={pendingCropImage.preview}
          aspectRatio="1:1"
          onCropComplete={handleCropComplete}
          minWidth={800}
          minHeight={800}
        />
      )}

      {/* Queue indicator */}
      {cropQueueLength > 0 && (
        <div className="fixed bottom-4 right-4 bg-primary text-primary-foreground px-4 py-2 rounded-full text-sm shadow-lg z-50">
          {cropQueueLength} image{cropQueueLength > 1 ? 's' : ''} en attente de recadrage
        </div>
      )}
    </>
  );
}
